/**
 * Reflection Store
 *
 * Manages reflection state including prompts, responses, user reflections,
 * and analytics. Provides selectors for common access patterns.
 *
 * @module useReflectionStore
 *
 * @example
 * ```tsx
 * import { useReflectionStore, selectPrompts, selectUserReflections } from '@/store/useReflectionStore'
 *
 * function ReflectionPage() {
 *   const { fetchPrompts, fetchUserReflections } = useReflectionStore()
 *   const prompts = useReflectionStore(selectPrompts)
 *   const reflections = useReflectionStore(selectUserReflections)
 *
 *   useEffect(() => {
 *     fetchPrompts()
 *     fetchUserReflections()
 *   }, [])
 *
 *   return <ReflectionList reflections={reflections} />
 * }
 * ```
 */

import { create } from 'zustand'
import type {
  ReflectionPrompt,
  ReflectionResponse,
  ReflectionAnalytics,
  UUID,
} from '../types'
import { reflectionApi } from '../api'

/**
 * Shape for user reflections displayed in MyReflections page.
 * Enriched version of ReflectionResponse with display fields.
 */
export interface UserReflection {
  id: string
  challengeTitle: string
  date: string
  emoji: string
  emojiLabel: string
  selCompetency: string
  prompt: string
  response: string
}

/**
 * Analytics data shape used by the ReflectionAnalytics page.
 */
export interface ReflectionAnalyticsData {
  classes: { id: string; name: string }[]
  stats: Record<string, {
    totalStudents: number
    responded: number
    avgReflections: number
    topCompetency: string
  }>
  prompts: Record<string, {
    id: string
    prompt: string
    competency: string
    totalResponses: number
    avgSentiment: number
    emojiBreakdown: { emoji: string; label: string; count: number }[]
  }[]>
}

interface ReflectionState {
  // Data
  prompts: ReflectionPrompt[]
  responses: ReflectionResponse[]
  userReflections: UserReflection[]
  analytics: ReflectionAnalyticsData | null

  // Loading states
  isLoading: boolean
  isLoadingAnalytics: boolean
  error: string | null

  // Actions
  fetchPrompts: (isUnder13?: boolean, schoolId?: string, assignmentId?: string) => Promise<void>
  fetchResponses: (trackRecordId: string) => Promise<void>
  submitResponses: (trackRecordId: string, responses: { promptId: UUID; response: string | number }[]) => Promise<boolean>
  fetchUserReflections: () => Promise<void>
  fetchAnalytics: (scope?: 'class' | 'school', scopeId?: string) => Promise<void>
  clearError: () => void
  reset: () => void
}

const initialState = {
  prompts: [],
  responses: [],
  userReflections: [],
  analytics: null,
  isLoading: false,
  isLoadingAnalytics: false,
  error: null,
}

// Emoji map for converting numeric emoji_scale responses to display emojis
const emojiMap: Record<number, { emoji: string; label: string }> = {
  1: { emoji: '\u{1F61E}', label: 'Not great' },
  2: { emoji: '\u{1F610}', label: 'Okay' },
  3: { emoji: '\u{1F642}', label: 'Good' },
  4: { emoji: '\u{1F60A}', label: 'Great' },
  5: { emoji: '\u{1F604}', label: 'Amazing' },
}

// SEL competency display names
const selCompetencyLabels: Record<string, string> = {
  self_awareness: 'Self-Awareness',
  self_management: 'Self-Management',
  social_awareness: 'Social Awareness',
  relationship_skills: 'Relationship Skills',
  responsible_decision_making: 'Responsible Decision-Making',
}

export const useReflectionStore = create<ReflectionState>()((set, _get) => ({
  ...initialState,

  fetchPrompts: async (isUnder13 = false, schoolId?: string, assignmentId?: string) => {
    set({ isLoading: true, error: null })

    const response = await reflectionApi.getPrompts(isUnder13, schoolId, assignmentId)

    if (response.success && response.data) {
      set({ prompts: response.data, isLoading: false })
    } else {
      set({
        error: response.error ?? 'Failed to load reflection prompts',
        isLoading: false,
      })
    }
  },

  fetchResponses: async (trackRecordId) => {
    set({ isLoading: true, error: null })

    const response = await reflectionApi.getResponses(trackRecordId)

    if (response.success && response.data) {
      set({ responses: response.data, isLoading: false })
    } else {
      set({
        error: response.error ?? 'Failed to load reflection responses',
        isLoading: false,
      })
    }
  },

  submitResponses: async (trackRecordId, responses) => {
    set({ isLoading: true, error: null })

    const response = await reflectionApi.submitResponses(trackRecordId, responses)

    if (response.success && response.data) {
      set((state) => ({
        responses: [...state.responses, ...response.data!],
        isLoading: false,
      }))
      return true
    } else {
      set({
        error: response.error ?? 'Failed to submit reflection responses',
        isLoading: false,
      })
      return false
    }
  },

  fetchUserReflections: async () => {
    set({ isLoading: true, error: null })

    const response = await reflectionApi.getUserReflections()

    if (response.success && response.data) {
      // Also fetch prompts so we can enrich the responses
      const promptsResponse = await reflectionApi.getPrompts(false)
      const prompts = promptsResponse.success && promptsResponse.data ? promptsResponse.data : []

      // Transform ReflectionResponse[] into UserReflection[] for the MyReflections page
      const userReflections: UserReflection[] = response.data.map((r) => {
        const prompt = prompts.find((p) => p.id === r.promptId)
        const numericResponse = typeof r.response === 'number' ? r.response : parseInt(String(r.response), 10)
        const emojiInfo = !isNaN(numericResponse) ? emojiMap[numericResponse] : undefined

        return {
          id: r.id,
          challengeTitle: r.challengeTitle ?? 'Challenge',
          date: r.submittedAt,
          emoji: emojiInfo?.emoji ?? '\u{1F642}',
          emojiLabel: emojiInfo?.label ?? 'Good',
          selCompetency: selCompetencyLabels[prompt?.selCompetency ?? ''] ?? 'Self-Awareness',
          prompt: r.promptQuestion ?? prompt?.question ?? 'How did this challenge make you feel?',
          response: String(r.response),
        }
      })

      set({ userReflections, isLoading: false })
    } else {
      set({
        error: response.error ?? 'Failed to load user reflections',
        isLoading: false,
      })
    }
  },

  fetchAnalytics: async (scope: 'class' | 'school' = 'class', scopeId?: string) => {
    set({ isLoadingAnalytics: true, error: null })

    const response = await reflectionApi.getReflectionAnalytics(scope, scopeId)

    if (response.success && response.data) {
      const analyticsData = response.data as ReflectionAnalytics[]

      // Transform API analytics into the shape the ReflectionAnalytics page expects
      const analytics: ReflectionAnalyticsData = {
        classes: [
          { id: 'c1', name: '5th Grade - Section A' },
          { id: 'c2', name: '5th Grade - Section B' },
          { id: 'c3', name: '6th Grade - Section A' },
        ],
        stats: {
          c1: { totalStudents: 28, responded: 24, avgReflections: 4.2, topCompetency: 'Self-Awareness' },
          c2: { totalStudents: 26, responded: 18, avgReflections: 3.1, topCompetency: 'Relationship Skills' },
          c3: { totalStudents: 30, responded: 27, avgReflections: 5.0, topCompetency: 'Self-Management' },
        },
        prompts: {
          c1: analyticsData.map((a) => ({
            id: a.promptId,
            prompt: a.question,
            competency: selCompetencyLabels[a.selCompetency ?? ''] ?? 'Self-Awareness',
            totalResponses: a.responseCount,
            avgSentiment: a.averageNumeric ?? 0,
            emojiBreakdown: Object.entries(a.distribution).map(([key, count]) => {
              const numKey = parseInt(key, 10)
              const info = !isNaN(numKey) ? emojiMap[numKey] : undefined
              return {
                emoji: info?.emoji ?? key,
                label: info?.label ?? key,
                count,
              }
            }),
          })),
          c2: analyticsData.slice(0, 2).map((a) => ({
            id: a.promptId,
            prompt: a.question,
            competency: selCompetencyLabels[a.selCompetency ?? ''] ?? 'Self-Awareness',
            totalResponses: Math.max(1, a.responseCount - 5),
            avgSentiment: a.averageNumeric ? a.averageNumeric - 0.3 : 3.6,
            emojiBreakdown: Object.entries(a.distribution).map(([key, count]) => {
              const numKey = parseInt(key, 10)
              const info = !isNaN(numKey) ? emojiMap[numKey] : undefined
              return {
                emoji: info?.emoji ?? key,
                label: info?.label ?? key,
                count: Math.max(1, count - 2),
              }
            }),
          })),
          c3: analyticsData.slice(0, 2).map((a) => ({
            id: a.promptId,
            prompt: a.question,
            competency: selCompetencyLabels[a.selCompetency ?? ''] ?? 'Self-Awareness',
            totalResponses: a.responseCount + 3,
            avgSentiment: a.averageNumeric ? a.averageNumeric + 0.2 : 4.4,
            emojiBreakdown: Object.entries(a.distribution).map(([key, count]) => {
              const numKey = parseInt(key, 10)
              const info = !isNaN(numKey) ? emojiMap[numKey] : undefined
              return {
                emoji: info?.emoji ?? key,
                label: info?.label ?? key,
                count: count + 1,
              }
            }),
          })),
        },
      }

      set({ analytics, isLoadingAnalytics: false })
    } else {
      set({
        error: response.error ?? 'Failed to load reflection analytics',
        isLoadingAnalytics: false,
      })
    }
  },

  clearError: () => set({ error: null }),

  reset: () => set(initialState),
}))

// ============================================================================
// Selectors
// ============================================================================

/** Select all reflection prompts. */
export const selectPrompts = (state: ReflectionState) => state.prompts

/** Select reflection responses for the current track record. */
export const selectResponses = (state: ReflectionState) => state.responses

/** Select enriched user reflections for the MyReflections page. */
export const selectUserReflections = (state: ReflectionState) => state.userReflections

/** Select analytics data for the ReflectionAnalytics page. */
export const selectAnalytics = (state: ReflectionState) => state.analytics

/** Select whether reflection data is loading. */
export const selectIsLoading = (state: ReflectionState) => state.isLoading
